import React, { useMemo } from 'react'
import { Row, Col, Card, Table, Progress, Statistic } from 'antd'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import useReportsData from '../../../hooks/useReportsData'
import styles from './StockTurnover.module.css'

const StockTurnoverTab = ({ dateRange }) => {
  const inventoryItems = useSelector((state) => state.inventory.items)
  const { turnoverAnalysis } = useReportsData(dateRange)
  const { items, categoryTurnover, healthyCounts, lowCounts, overallAverageTurnover } = turnoverAnalysis

  const turnoverData = useMemo(() => {
    return inventoryItems
      .map((item) => {
        const soldQuantity = item.totalSold || 0
        const averageInventory = (item.currentStock + (item.initialStock || item.currentStock)) / 2
        const turnoverRate = averageInventory > 0 ? soldQuantity / averageInventory : 0

        return {
          ...item,
          soldQuantity,
          averageInventory: Math.round(averageInventory),
          turnoverRate: Number(turnoverRate.toFixed(2)),
          turnoverHealth: turnoverRate > 1 ? 'Healthy' : turnoverRate > 0.5 ? 'Moderate' : 'Low'
        }
      })
      .sort((a, b) => b.turnoverRate - a.turnoverRate)
  }, [inventoryItems])

  // Format category data for the chart
  const categoryChartData = useMemo(() => {
    return categoryTurnover.map((category) => ({
      name: category.name,
      turnoverRate: category.turnoverRate
    }))
  }, [categoryTurnover])

  const columns = [
    {
      title: 'Item Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => a.category.localeCompare(b.category)
    },
    {
      title: 'Turnover Rate',
      key: 'turnoverRate',
      render: (_, record) => (
        <Progress
          percent={Math.min(record.turnoverRate * 100, 100)}
          size='small'
          strokeColor={record.turnoverRate >= 1.5 ? '#52c41a' : record.turnoverRate >= 0.8 ? '#faad14' : '#ff4d4f'}
          format={(percent) => `${record.turnoverRate.toFixed(2)}x`}
        />
      ),
      sorter: (a, b) => a.turnoverRate - b.turnoverRate
    },
    {
      title: 'Sold Quantity',
      dataIndex: 'soldQuantity',
      key: 'soldQuantity',
      sorter: (a, b) => a.soldQuantity - b.soldQuantity
    },
    {
      title: 'Average Inventory',
      dataIndex: 'averageInventory',
      key: 'averageInventory',
      sorter: (a, b) => a.averageInventory - b.averageInventory
    },
    {
      title: 'Status',
      key: 'turnoverHealth',
      dataIndex: 'turnoverHealth',
      render: (status) => {
        const color = status === 'Healthy' ? '#52c41a' : status === 'Moderate' ? '#faad14' : '#ff4d4f'
        return <span style={{ color }}>{status}</span>
      },
      sorter: (a, b) => a.turnoverHealth.localeCompare(b.turnoverHealth)
    }
  ]

  const summaryData = useMemo(() => {
    return {
      averageTurnover: overallAverageTurnover || 0,
      totalItems: items.length,
      healthyItems: healthyCounts || 0,
      lowItems: lowCounts || 0
    }
  }, [items.length, healthyCounts, lowCounts, overallAverageTurnover])

  return (
    <div className={styles.stockTurnoverContainer}>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} md={6}>
          <Card className={styles.statsCard}>
            <Statistic
              title='Average Turnover Rate'
              value={summaryData.averageTurnover}
              suffix='x'
              precision={2}
              valueStyle={{
                color:
                  summaryData.averageTurnover >= 1.5
                    ? '#52c41a'
                    : summaryData.averageTurnover >= 0.8
                    ? '#faad14'
                    : '#ff4d4f'
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className={styles.statsCard}>
            <Statistic title='Total Items' value={summaryData.totalItems} />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className={styles.statsCard}>
            <Statistic title='Healthy Turnover' value={summaryData.healthyItems} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className={styles.statsCard}>
            <Statistic title='Low Turnover' value={summaryData.lowItems} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className={styles.chartRow}>
        <Col span={24}>
          <Card title='Category Turnover Analysis' className={styles.turnoverChartCard}>
            <ResponsiveContainer width='100%' height={400}>
              <BarChart data={categoryChartData} barSize={60}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis domain={[0, 'dataMax + 0.5']} />
                <Tooltip formatter={(value) => [`${value.toFixed(2)}x`, 'Turnover Rate']} />
                <Bar
                  dataKey='turnoverRate'
                  fill='#1890ff'
                  name='Turnover Rate'
                  label={{
                    position: 'top',
                    formatter: (value) => `${value.toFixed(2)}x`
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className={styles.tableRow}>
        <Col span={24}>
          <Card title='Stock Turnover Details' className={styles.turnoverTableCard}>
            <Table dataSource={items} columns={columns} rowKey='id' pagination={false} scroll={{ x: true }} />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default StockTurnoverTab
